import React, { useMemo } from 'react';
import webCore from '@happylife-a/web-core';
import { Flex, Box, Button } from '@chakra-ui/react';
import * as localization from '@happylife-a/localization';
import AutoCompleteDropdown from '../AutoCompleteDropdown';

export default function CategoryFilters({
  filters,
  onCategoryChange,
  onSubCategoryChange,
  onSubSubCategoryChange,
  onClearFilters,
}) {
  const { t, locale } = webCore.contexts.useTranslation();
  const { categories, getChildren } = webCore.contexts.useCategories();

  const parentCategories = useMemo(() => {
    return categories.filter((category) => !category.parentId);
  }, [categories]);

  const subCategories = useMemo(() => {
    if (!filters?.parentCategoryId) {
      return null;
    }
    const children = getChildren(filters.parentCategoryId);
    return children && children.length > 0 ? children : null;
  }, [getChildren, filters?.parentCategoryId]);

  const subSubCategories = useMemo(() => {
    if (!filters?.subCategoryId) {
      return null;
    }
    const children = getChildren(filters.subCategoryId);
    return children && children.length > 0 ? children : null;
  }, [getChildren, filters?.subCategoryId]);

  const hasActiveFilters = useMemo(() => {
    return !!(filters?.parentCategoryId || filters?.subCategoryId || filters?.subSubCategoryId);
  }, [filters?.parentCategoryId, filters?.subCategoryId, filters?.subSubCategoryId]);

  return (
    <Flex
      gap={2}
      alignItems="center"
      flexWrap="wrap"
      direction={{ base: 'column', md: 'row' }}
      width={{ base: '100%', md: 'auto' }}
    >
      <Box width={{ base: '100%', md: '200px' }}>
        <AutoCompleteDropdown
          formControlBottomSpacing={0}
          placeholder={t('Category')}
          value={filters?.parentCategoryId || 0}
          onChange={(value) => onCategoryChange(value || '')}
          options={parentCategories}
          extractor={(item) => ({
            value: item.id,
            label: localization.localize(item.name, locale),
          })}
        />
      </Box>

      {subCategories && (
        <Box width={{ base: '100%', md: '200px' }}>
          <AutoCompleteDropdown
            formControlBottomSpacing={0}
            placeholder={t('AddSubCategory')}
            value={filters?.subCategoryId || 0}
            onChange={(value) => onSubCategoryChange(value || '')}
            options={subCategories}
            extractor={(item) => ({
              value: item.id,
              label: localization.localize(item.name, locale),
            })}
          />
        </Box>
      )}

      {subSubCategories && (
        <Box width={{ base: '100%', md: '200px' }}>
          <AutoCompleteDropdown
            formControlBottomSpacing={0}
            placeholder={t('AddSubSubCategories')}
            value={filters?.subSubCategoryId || 0}
            onChange={(value) => onSubSubCategoryChange(value || '')}
            options={subSubCategories}
            extractor={(item) => ({
              value: item.id,
              label: localization.localize(item.name, locale),
            })}
          />
        </Box>
      )}

      {hasActiveFilters && (
        <Button
          size="sm"
          colorScheme="red"
          borderRadius="8px"
          variant="defaultBig"
          h="40px"
          width={{ base: '100%', md: 'auto' }}
          onClick={onClearFilters}
        >
          {t('Clear')}
        </Button>
      )}
    </Flex>
  );
}
